<template>
  <div class="editCamsSetting">
    <section v-if="!isNew && !cam" class="d-flex justify-center mt-3">
      <v-progress-circular
        :size="30"
        :width="2"
        indeterminate
      ></v-progress-circular>
    </section>
    <template v-else>
      <setting-header class="editCamsSetting_header" />
      <v-divider class="editCamsSetting_divider" />
      <span class="editCamsSetting_name">名前</span>
      <v-text-field
        v-model="name"
        height="40px"
        color="dark"
        class="editCamsSetting_input-name"
        required
        dense
        outlined
        full-width
        :maxlength="50"
        @blur="handleCameraName"
        @keyup="validateInput"
      />
      <template v-if="false">
        <span class="editCamsSetting_name">説明文</span>
        <v-text-field
          :value="'老舗書店 銀座支店のカメラ一覧'"
          height="40px"
          color="dark"
          class="editCamsSetting_input-name"
          required
          dense
          outlined
        />
      </template>
      <div v-if="!isNew" class="editCamsSetting_footer">
        <h3>このグループを削除する</h3>
        <v-btn
          text
          icon
          class="editCamsSetting_btn-delete"
          @click="handleDelete"
        >
          <span class="icon icon-trash" />
        </v-btn>
      </div>
      <footer>
        <v-divider class="editCamsSetting_divider" />
        <section class="editCamsSetting_actions">
          <v-btn
            class="editCamsSetting_submit"
            :loading="submitLoading"
            :disabled="!name.length || !isValid"
            @click="handleSubmit"
            >{{ isNew ? '作成する' : '更新する' }}</v-btn
          >
        </section>
      </footer>
      <!-- Dialogs -->
      <!-- Delete dialog -->
      <clip-dialog-a
        :open="dialogDeleteOpen"
        title="グループの削除"
        @onClose="closeDialogDelete"
      >
        <div class="editCamsSetting_dialog-content">
          このグループを完全に削除しても<br />
          よろしいですか？
        </div>
        <template #actions>
          <v-btn
            class="editCamsSetting_dialog-btn editCamsSetting_dialog-cancel"
            color="text"
            @click="closeDialogDelete"
          >
            キャンセル
          </v-btn>
          <v-spacer />
          <v-btn
            class="editCamsSetting_dialog-btn editCamsSetting_dialog-delete"
            color="text"
            :loading="deleteLoading"
            @click="handleDeleteOk"
          >
            削除する
          </v-btn>
        </template>
      </clip-dialog-a>
      <!-- ----------------- -->
      <!-- Message dialog -->
      <clip-dialog-message
        :open="!!dialogMessageText"
        :message="dialogMessageText"
        @onClose="closeDialogMessage"
      />
      <!-- ----------------- -->
    </template>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import ClipDialogA from '@/components/dialogs/ClipDialogA.vue';
import ClipDialogMessage from '@/components/dialogs/ClipDialogMessage.vue';
import SettingHeader from '../SettingHeader.vue';
import { dialogService } from '@/services/dialog.service';
import {
  getFullWidthNumericHyphenUnderbar,
  validateInputName,
} from '@/utils/helpers';

export default {
  name: 'EditCamsSetting',
  components: {
    SettingHeader,
    ClipDialogA,
    ClipDialogMessage,
  },
  data() {
    return {
      dialogDeleteOpen: false,
      dialogMessageText: '',
      submitLoading: false,
      deleteLoading: false,
      name: '',
      isValid: false,
    };
  },
  computed: {
    id() {
      return this.$route.params.id;
    },
    isNew() {
      return this.$route.name === 'settings-cams-new';
    },
    ...mapState({
      groupCams: (state) => state.settings.groupCams,
    }),
    cam() {
      if (this.id) {
        return this.groupCams.find((cam) => String(cam.id) === String(this.id));
      }
      return {
        title: '',
      };
    },
  },
  watch: {
    dialogDeleteOpen(val) {
      val || this.closeDialogDelete();
    },
    dialogMessageText(val) {
      val || this.closeDialogMessage();
    },
    cam: {
      handler: function (val) {
        if (val) {
          this.name = val.title;
        }
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    ...mapActions('settings', [
      'createCameraGroup',
      'updateCameraGroupSetting',
      'deleteCameraGroup',
      'fetchCameraGroup',
    ]),
    validateInput() {
      this.isValid = validateInputName(this.name).isValid;
    },
    handleCameraName() {
      if (!this.name) {
        return;
      }
      this.name = getFullWidthNumericHyphenUnderbar(this.name);
      this.isValid = validateInputName(this.name).isValid;
    },
    async handleSubmit() {
      try {
        this.submitLoading = true;
        if (this.isNew) {
          await this.createCameraGroup({
            name: this.name,
            shareScope: 1,
          });

          await this.fetchCameraGroup();
          const route = {
            name: 'settings-cams-list',
            params: { group: 1 },
            query: { scroll: this.name },
          };

          this.$router.push(route);
        } else {
          await this.updateCameraGroupSetting({
            name: this.name,
            id: this.id,
            shareScope: 1,
          });
          await this.fetchCameraGroup();
          this.$router.push({
            name: 'settings-cams-list',
            params: { group: this.$route.params.group, id: this.id },
          });
        }
      } catch (error) {
        if (error.status === 400 && error.errorCode === 14) {
          dialogService.showErrorDialog(
            {
              title: 'カメラグループ名重複エラー',
              content: `
カメラグループ名が重複しています。<br>
別のカメラグループ名にて登録してください。`,
              cancel: false,
            },
            {
              classes: ['v--modal'],
              width: 500,
              height: 227,
            }
          );
        }
        console.error(error);
      } finally {
        this.submitLoading = false;
      }
    },
    handleDelete() {
      this.dialogDeleteOpen = true;
    },
    async handleDeleteOk() {
      try {
        this.deleteLoading = true;
        await this.deleteCameraGroup({ ids: [this.id] });
        await this.fetchCameraGroup();
        this.closeDialogDelete();
        this.$router.push({ name: 'settings-cams-list', query: { dl: 1 } }); // set delete flag to true in query string to display deleted message in list page
      } catch (error) {
        console.error(error);
      } finally {
        this.deleteLoading = false;
      }
    },
    closeDialogDelete() {
      this.dialogDeleteOpen = false;
    },
    closeDialogMessage() {
      this.dialogMessageText = null;
    },
  },
};
</script>

<style lang="scss">
.editCamsSetting {
  &_divider {
    &.theme--light.v-divider {
      border-color: #ccc;
    }
  }
  &_header {
    margin-bottom: 16px;
  }
  &_gap-header {
    height: 26px;
  }
  &_name {
    font-size: 14px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    letter-spacing: normal;
    text-align: left;
    color: #666;
    margin-top: 10px;
    margin-bottom: 10px;
    height: 20px;
    display: block;
  }
  &_input-name {
    margin-top: 10px !important;
    max-width: 500px;
    &.theme--light.v-input {
      input {
        font-size: 16px;
        font-weight: normal;
        font-stretch: normal;
        font-style: normal;
        line-height: 1.38;
        letter-spacing: normal;
        text-align: left;
        color: #4b4b4b;
      }
    }
  }
  &_footer {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    border-top: 1px solid #e6e6e6;
    padding: 9px 0;
    h3 {
      font-size: 14px;
      font-weight: bold;
      font-stretch: normal;
      font-style: normal;
      letter-spacing: normal;
      text-align: left;
      color: #4b4b4b;
    }
  }
  &_actions {
    display: flex;
    justify-content: space-between;
    margin-top: 10px;
  }
  &_submit {
    &.theme--light.v-btn.v-btn--has-bg {
      width: 120px;
      height: 36px;
      border-radius: 5px;
      box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.16);
      background-color: #0075c2;
      font-size: 14px;
      font-weight: bold;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.14;
      letter-spacing: normal;
      text-align: left;
      color: #fff;
    }
    &.theme--light.v-btn.v-btn--disabled.v-btn--has-bg {
      background-color: #f3f3f3 !important;
      box-shadow: none;
      color: #ccc !important;
    }
  }
  &_btn-delete {
    .icon-trash {
      font-size: 16px;
      color: #989898;
    }
  }
  &_dialog {
    position: relative;

    &-cancel {
      color: #989898 !important;
    }
    &-delete {
      color: #eb545b !important;
    }
    &-btn {
      width: 120px;
      font-size: 14px !important;
      font-weight: normal;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.43;
      letter-spacing: normal;
      text-align: center;
      border-radius: 5px;
      box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.16);
    }
    &-content {
      margin: 20px 0;
      font-size: 14px;
      font-weight: normal;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.43;
      letter-spacing: normal;
      text-align: center;
      color: #666;
    }
  }
}
</style>
